import React, { useEffect, useState } from 'react';
import { adminGetSettings, adminUpdateSetting, adminSyncCapacity } from '../../api';

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 4 },
    sub:   { color: '#6b7280', fontSize: 13, marginBottom: 24 },
    grid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' },
    box:   { background: '#fff', borderRadius: 10, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    boxH:  { fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#1a1d23' },
    row:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f9fafb' },
    key:   { fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: 'monospace' },
    desc:  { fontSize: 12, color: '#9ca3af', marginTop: 2 },
    inp:   { width: 120, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, textAlign: 'right' },
    btn:   { padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    sync:  { width: '100%', padding: '12px 0', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14, marginTop: 20 },
    note:  { padding: '14px 16px', background: '#f0f4ff', borderRadius: 8, fontSize: 12, color: '#4b5563', lineHeight: 1.7, marginTop: 16 },
};

export default function AdminSettings() {
    const [settings, setSettings] = useState([]);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState({});
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminGetSettings()
            .then(r => {
                setSettings(r.data);
                const v = {};
                r.data.forEach(s => { v[s.key] = s.value; });
                setValues(v);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const save = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            await adminUpdateSetting(key, values[key]);
            load();
        } catch (e) {
            alert('Failed to save: ' + (e.response?.data?.error || e.message));
        } finally {
            setSaving(prev => ({ ...prev, [key]: false }));
        }
    };

    const doSync = async () => {
        setSyncing(true);
        try {
            await adminSyncCapacity();
            alert('✅ Capacity synced! max_qty for all services has been updated based on current active accounts.');
        } catch (e) {
            alert('Sync failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setSyncing(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div>
            <h1 style={s.h1}>Panel Settings</h1>
            <div style={s.sub}>Configure panel behavior. Changes take effect immediately.</div>

            <div style={s.grid}>
                {/* Settings list */}
                <div style={s.box}>
                    <div style={s.boxH}>⚙️ Configuration</div>
                    {settings.map(setting => (
                        <div key={setting.key} style={s.row}>
                            <div>
                                <div style={s.key}>{setting.key}</div>
                                <div style={s.desc}>{setting.description}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                    style={s.inp}
                                    value={values[setting.key] ?? ''}
                                    onChange={e => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                                    onKeyDown={e => e.key === 'Enter' && save(setting.key)}
                                />
                                <button
                                    style={{ ...s.btn, opacity: saving[setting.key] ? .6 : 1 }}
                                    onClick={() => save(setting.key)}
                                    disabled={saving[setting.key]}
                                >
                                    {saving[setting.key] ? '...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Capacity sync */}
                <div style={s.box}>
                    <div style={s.boxH}>📊 Capacity Management</div>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                        <strong>capacity_pct</strong> controls the maximum order quantity for each service.
                        It is computed as a percentage of ACTIVE accounts,
                        filtered by gender and country.
                    </p>
                    <div style={s.note}>
                        Example with <strong>capacity_pct = 60</strong> and <strong>45 female accounts in IT</strong>:<br />
                        → max_qty for female services (IT) = floor(45 × 60 / 100) = <strong>27</strong><br /><br />
                        Run <em>Sync Capacity</em> after changing the setting or adding new accounts.
                        This updates all services' max_qty live.
                    </div>
                    <button style={{ ...s.sync, opacity: syncing ? .6 : 1 }} onClick={doSync} disabled={syncing}>
                        {syncing ? 'Syncing...' : '🔄 Sync Capacity Now'}
                    </button>
                    <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
                        Capacity also auto-syncs every 5 minutes in the background.
                    </p>
                </div>
            </div>
        </div>
    );
}
